import { Page } from '@epilot/customer-portal-client'

import { findBlockByType } from 'utils/builder'
import { getCurrentLanguage } from 'utils/LanguageUtils'

interface FooterLink {
  label?: Record<string, string>
  url?: Record<string, string>
}

const UNRELATED_KEYWORDS = [
  'homepage',
  'instagram',
  'facebook',
  'linkedin',
  'xing',
  'youtube',
  'kununu'
]

/**
 * Extracts page footer block links related to cookie consent
 * @param page - The current page object containing blocks
 * @returns Array of footer links or empty array if no footer block found
 */
export const getFooterLinksFromPage = (
  page: Page | undefined
): FooterLink[] => {
  if (!page?.blocks) {
    return []
  }

  const footerBlock = findBlockByType(page.blocks, 'footer')

  if (!footerBlock) {
    return []
  }

  const links = footerBlock.props?.content?.links || []

  const relevantLinks = links.filter((link) => {
    const label = link.label?.[getCurrentLanguage()]

    return !UNRELATED_KEYWORDS.some((keyword) =>
      label?.toLowerCase().includes(keyword)
    )
  })

  return relevantLinks
}

/**
 * Extracts links related to cookie consent from HTML content
 * @param htmlContent - HTML string content
 * @returns Array of extracted links with text and href
 */
export const extractLinksFromHTML = (htmlContent: string): FooterLink[] => {
  if (!htmlContent) {
    return []
  }

  try {
    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div')

    // We are getting rid of the old design with HTML content
    // eslint-disable-next-line no-unsanitized/property
    tempDiv.innerHTML = htmlContent

    const anchorTags = tempDiv.querySelectorAll('a')

    const links: FooterLink[] = []

    anchorTags.forEach((anchor) => {
      const href = anchor.href || anchor.getAttribute('href')
      const text = anchor.textContent?.trim()

      const isRelevant = !UNRELATED_KEYWORDS.some((keyword) =>
        text?.toLowerCase().includes(keyword)
      )

      if (href && text && isRelevant) {
        // Create a simple link object - using current language for extracted links
        links.push({
          label: { [getCurrentLanguage()]: text },
          url: { [getCurrentLanguage()]: href }
        })
      }
    })

    return links
  } catch (error) {
    console.warn('Failed to extract links from HTML:', error)

    return []
  }
}
